/* ---------------------------------------------------------------------------
 * Club 1984 — built by Fabrik
 * https://fabrik.ae
 *
 * Growth studio for fashion and footwear brands.
 * Design, build and merchandising systems on Shopify.
 * ------------------------------------------------------------------------- */

/* Club 1984 — Complete the look (§4.11, Stage 2).
 *
 * Runs for every instance on the page: the product page renders one, the cart
 * drawer renders another, and the drawer re-renders itself on every cart
 * change. So everything is delegated from the document and scoped to the
 * nearest root, rather than bound to one element at load.
 *
 * Each piece is added on its own — a customer taking just the cap has no
 * reason to be committed to the rest of the look.
 *
 * The drawer's free shipping picks use the same cards and the same add path;
 * a root carrying data-c84-source is reported under that source instead.
 */
(function () {
  /* Several blocks include this file (the product page, the drawer's Complete
     the look, the drawer's free shipping picks). Everything is delegated from
     the document, so it only needs to run once. */
  if (window.c84LookBound) return;
  window.c84LookBound = true;

  const PREFERRED = 'M';
  const rootOf = (el) => el.closest('[data-c84-look]');

  /* In the drawer there is no buy box; the size to favour is the one the
     customer just added, which Liquid puts on the section. */
  function wantedSize(root) {
    const fromCart = root && root.dataset.c84CartSize;
    return fromCart || buyBoxSize();
  }

  function buyBoxSize() {
    const on = document.querySelector(
      '.product-column-right .opt-btn[aria-checked="true"], .product-column-right .opt-btn.is-selected'
    );
    if (on) return on.textContent.trim();
    const checked = document.querySelector('.product-column-right .opt-btn:checked');
    return checked ? (checked.value || '').split(' / ')[0].trim() : null;
  }

  /* Pre-select a size so the add button is never a dead end. */
  function pick(card) {
    if (!card) return;
    const chips = Array.from(card.querySelectorAll('.c84-look__chip'));
    const usable = chips.filter((c) => !c.hasAttribute('disabled'));
    if (!usable.length) return;
    if (card.querySelector('.c84-look__chip--only')) return;   /* one-size, already set */

    chips.forEach((c) => c.setAttribute('aria-pressed', 'false'));
    const wanted = wantedSize(card.closest('[data-c84-look]'));
    const byName = (n) => usable.find((c) => (c.dataset.size || '').toUpperCase() === String(n).toUpperCase());
    ((wanted && byName(wanted)) || byName(PREFERRED) || usable[0]).setAttribute('aria-pressed', 'true');
  }

  const visibleCards = (scope) =>
    Array.from((scope || document).querySelectorAll('[data-c84-look-slot]'))
      .map((s) => s.querySelector('.c84-look__card:not(.c84-look__card--hidden)'))
      .filter(Boolean);

  /* Which products are in the bag, read off the cart rows themselves.

     The drawer used to do this in Liquid, but that made the rendered markup
     depend on the cart, which meant the cross-sell block's cache key changed on
     every add and Symmetry rebuilt all eight cards - and all eight images -
     each time. The rows are re-rendered on every add anyway, so reading them is
     always current and costs nothing. */
  function cartHandles() {
    const set = new Set();
    document.querySelectorAll('cart-drawer .cart-item__image-link[href*="/products/"]').forEach((a) => {
      const m = (a.getAttribute('href') || '').match(/\/products\/([^?#\/]+)/);
      if (m) set.add(m[1]);
    });
    return set;
  }

  /* A piece the customer has already put in the bag stops being a suggestion.
     Where a slot has alternates, it falls through to one still available rather
     than going blank. */
  function syncToCart() {
    const inCart = cartHandles();
    document.querySelectorAll('cart-drawer [data-c84-look-slot]').forEach((slot) => {
      const cards = Array.from(slot.querySelectorAll('.c84-look__card'));
      if (!cards.length) return;
      const free = cards.filter((c) => !inCart.has(c.dataset.c84Handle));

      if (!free.length) { slot.classList.add('c84-look__slot--taken'); return; }
      slot.classList.remove('c84-look__slot--taken');

      const showing = cards.find((c) => !c.classList.contains('c84-look__card--hidden'));
      if (showing && inCart.has(showing.dataset.c84Handle)) {
        cards.forEach((c) => c.classList.add('c84-look__card--hidden'));
        free[0].classList.remove('c84-look__card--hidden');
        pick(free[0]);
      }
    });
  }

  function initAll() { syncToCart(); visibleCards().forEach(pick); }

  

  /* Standard Symmetry add-to-cart. Nothing is drawn locally and nothing is
     removed locally, so there is no window in which the drawer can show a
     piece it does not have.

     Three attempts at an instant row all ended the same way - the piece
     appeared and then went again. The row itself was not the fault; the
     sections were being asked for by name when Shopify keys them by section
     id, so the merge returned nothing and the placeholder was cleaned up over
     an empty list. But every version that draws its own row has that failure
     mode available to it, and a bag that briefly loses a piece costs more than
     a second of waiting. So: the drawer opens when the cart actually has the
     piece in it, the same as every other add-to-cart in this theme.

     Both the header and the drawer are refreshed, by section id, exactly as
     Symmetry's own product form does it in main.js. */
  async function addOne(button) {
    const root = rootOf(button);
    const card = button.closest('.c84-look__card');
    if (!card || card.dataset.c84Busy === '1') return;
    card.dataset.c84Busy = '1';

    /* one-size pieces carry the variant on their chip */
    const chip = card.querySelector('.c84-look__chip[aria-pressed="true"]');
    const variantId = chip ? chip.dataset.variantId : button.dataset.variantId;
    if (!variantId || !root) { card.dataset.c84Busy = '0'; return; }

    const label = button.dataset.label || button.textContent.trim();
    button.disabled = true;
    button.textContent = 'Adding';

    const sectionsToUpdate = ['page-header', 'cart-drawer']
      .map(function (sel) { return document.querySelector(sel); })
      .filter(function (el) { return el && el.dataset.sectionId; });
    const sectionIds = sectionsToUpdate.map(function (el) { return el.dataset.sectionId; });

    const body = new FormData();
    body.append('id', variantId);
    body.append('quantity', 1);
    body.append('sections_url', window.location.pathname);
    body.append('sections', sectionIds.join(','));

    try {
      const res = await fetch(root.dataset.cartAddUrl, {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: body,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(res.status);

      if (data.sections && sectionsToUpdate.length === Object.keys(data.sections).length) {
        sectionsToUpdate.forEach(function (el) {
          el.updateFromCartChange(data.sections[el.dataset.sectionId]);
        });
      } else {
        /* Shopify has been observed to return a null section. Symmetry falls
           back to its own cart change event; so does this. */
        button.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
      }

      button.textContent = 'Added';

      /* ---- tracking -------------------------------------------------------

         Two channels, because they cannot see each other.

         A Shopify app pixel runs in a sandboxed iframe: it has no access to
         dataLayer, to window, or to anything in the page. The only way a theme
         can reach it is Shopify.analytics.publish, which app pixels receive
         through analytics.subscribe. GTM is the opposite - it lives in the page
         and never sees the pixel's stream. So both are emitted.

         Shopify's own product_added_to_cart fires for this add anyway, since it
         goes through the Ajax cart API - but it carries no notion of which
         piece of UI caused it, which is the whole question being asked here.

         Figures come from the add.js response rather than the card, because the
         card's price can be stale after a stock or price change; the response is
         what Shopify actually recorded. Prices are in minor units.

         Wrapped so tracking can never break an add to cart. */
      try {
        var inDrawer = !!button.closest('cart-drawer');
        /* A root can name its own source - the drawer's free shipping picks
           do - so its adds are not counted as Complete the look. */
        var ownSource = root.dataset.c84Source;
        var payload = {
          source: ownSource || (inDrawer ? 'complete_the_look_drawer' : 'complete_the_look_pdp'),
          placement: ownSource ? ownSource.replace(/_/g, ' ') : (inDrawer ? 'cart drawer upsell' : 'pdp carousel'),
          variant_id: String(data.variant_id || data.id || variantId),
          product_id: String(data.product_id || ''),
          title: data.product_title || data.title || '',
          variant_title: data.variant_title || '',
          quantity: data.quantity || 1,
          value: (data.final_line_price != null ? data.final_line_price : data.price || 0) / 100,
          currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'GBP'
        };

        /* the app pixel */
        if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
          Shopify.analytics.publish('c84_add_to_cart', payload);
        }

        /* GTM, in GA4 shape */
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'add_to_cart',
          source: payload.source,
          currency: payload.currency,
          value: payload.value,
          items: [{
            item_id: payload.variant_id,
            item_name: payload.title,
            item_variant: payload.variant_title,
            item_list_name: payload.placement,
            price: payload.value,
            quantity: payload.quantity
          }]
        });

        /* and Symmetry's own event, so anything already listening behaves the
           same for our adds as for the buy box */
        button.dispatchEvent(new CustomEvent('on:cart:add', {
          bubbles: true,
          detail: { source: payload.source, item: data }
        }));
      } catch (e) { /* never let tracking stop a sale */ }
      syncToCart();

      /* Opened last, on content that is already correct. */
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
    } catch (err) {
      button.textContent = 'Try again';
    } finally {
      setTimeout(function () {
        button.textContent = label;
        button.disabled = false;
        card.dataset.c84Busy = '0';
      }, 900);
    }
  }

  document.addEventListener('click', (e) => {
    if (!e.target.closest || !rootOf(e.target)) return;

    /* In the drawer the customer is on their way to checkout, so following a
       suggestion through to its product page sends them a step backwards. The
       size row is the control here. On the product page the links stay live,
       because browsing is what that page is for. */
    const link = e.target.closest('.c84-look__media, .c84-look__name');
    if (link && rootOf(link).classList.contains('c84-look--drawer')) {
      e.preventDefault();
      return;
    }

    const chip = e.target.closest('.c84-look__chip');
    if (chip && !chip.hasAttribute('disabled')) {
      const card = chip.closest('.c84-look__card');
      card.querySelectorAll('.c84-look__chip').forEach((c) => c.setAttribute('aria-pressed', 'false'));
      chip.setAttribute('aria-pressed', 'true');

      /* In the drawer the size row is the control: tapping a size adds that
         size. On the product page it only selects, and the button adds. */
      if (rootOf(chip).classList.contains('c84-look--drawer')) {
        if (card.dataset.c84Busy === '1') return;
        const btn = card.querySelector('[data-c84-look-add-one]');
        if (btn) addOne(btn);
      }
      return;
    }

    const add = e.target.closest('[data-c84-look-add-one]');
    if (add) { addOne(add); return; }

    const sw = e.target.closest('[data-c84-look-switch]');
    if (sw) {
      const slot = sw.closest('[data-c84-look-slot]');
      const cards = Array.from(slot.querySelectorAll('.c84-look__card'));
      const cur = cards.findIndex((c) => !c.classList.contains('c84-look__card--hidden'));
      const next = (cur + 1) % cards.length;
      cards.forEach((c, i) => c.classList.toggle('c84-look__card--hidden', i !== next));
      pick(cards[next]);
    }
  });

  /* Follow the buy box: changing size re-picks defaults across the look. */
  document.addEventListener('change', (e) => {
    if (e.target.closest && e.target.closest('.opt-btn, .option-selector')) setTimeout(initAll, 60);
  });

  /* Symmetry's own add-to-cart on the product page changes the bag without
     going through this file, and the cross-sell block is no longer replaced on
     every add, so the mutation observer alone would never hear about it. */
  document.addEventListener('on:cart:change', () => setTimeout(initAll, 0));

  /* Still needed for the first time the block is injected. */
  const observer = new MutationObserver((records) => {
    for (const r of records) {
      for (const node of r.addedNodes) {
        if (node.nodeType === 1 && ((node.matches && node.matches('[data-c84-look]')) || (node.querySelector && node.querySelector('[data-c84-look]')))) {
          initAll();
          return;
        }
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  initAll();
})();
